import React, { useId } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePicker.css';

interface DatePickerProps {
    id?: string;
    name: string;
    value: string | null;
    onChange: (value: string) => void;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    min?: string;
    max?: string;
    error?: string;
    helperText?: string;
    className?: string;
    placeholder?: string;
    'aria-describedby'?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({
    id: providedId,
    name,
    value,
    onChange,
    label,
    required = false,
    disabled = false,
    min,
    max,
    error,
    helperText,
    className = '',
    placeholder = 'MM/DD/YYYY',
    'aria-describedby': ariaDescribedBy
}) => {
    const generatedId = useId();
    const id = providedId || generatedId;
    const errorId = `${id}-error`;
    const helperId = `${id}-helper`;

    // Parse YYYY-MM-DD to Date
    const parseISODate = (dateStr: string | null): Date | null => {
        if (!dateStr) return null;
        const [year, month, day] = dateStr.split('-').map(Number);
        if (year && month && day) {
            return new Date(year, month - 1, day);
        }
        return null;
    };

    // Format Date to YYYY-MM-DD
    const formatDateISO = (date: Date | null): string => {
        if (!date) return '';
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
    };

    const selectedDate = parseISODate(value);
    const minDate = min ? parseISODate(min) || undefined : undefined;
    const maxDate = max ? parseISODate(max) || undefined : undefined;

    const handleChange = (date: Date | Date[] | null, event?: React.SyntheticEvent<any> | undefined) => {
        // Handle single date selection (not array)
        const singleDate = Array.isArray(date) ? date[0] : date;
        onChange(formatDateISO(singleDate));
    };

    const describedByIds = [
        error && errorId,
        helperText && helperId,
        ariaDescribedBy
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={`flex flex-col gap-1 ${className}`}>
            <div className="datepicker-wrapper">
                <ReactDatePicker
                    id={id}
                    name={name}
                    selected={selectedDate}
                    onChange={handleChange}
                    dateFormat="MM/dd/yyyy"
                    placeholderText={placeholder}
                    disabled={disabled}
                    required={required}
                    minDate={minDate}
                    maxDate={maxDate}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    yearDropdownItemNumber={30}
                    scrollableYearDropdown
                    todayButton="Today"
                    showIcon
                    toggleCalendarOnIconClick
                    icon={
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    }
                    className={`
                        w-full px-3 py-2 border rounded-md shadow-sm
                        focus:outline-none focus:ring-2 focus:ring-offset-0
                        disabled:bg-gray-100 disabled:cursor-not-allowed
                        ${error
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-600 focus:ring-blue-500'
                        }
                    `}
                    wrapperClassName="w-full"
                    calendarClassName="datepicker-calendar-full-width"
                    aria-invalid={error ? 'true' : 'false'}
                    aria-describedby={describedByIds || undefined}
                    aria-required={required ? 'true' : 'false'}
                />
            </div>

            {helperText && !error && (
                <p
                    id={helperId}
                    className="text-xs text-gray-600"
                    role="status"
                >
                    {helperText}
                </p>
            )}
            {error && (
                <p
                    id={errorId}
                    className="text-xs text-red-600"
                    role="alert"
                    aria-live="polite"
                >
                    {error}
                </p>
            )}
        </div>
    );
};

export default DatePicker;
